// src/pages/SolicitacaoServico.jsx
import React, {useState, useEffect, useRef} from 'react';
import {
    Box,
    TextField,
    Button,
    MenuItem,
    Divider,
    Paper,
    Typography,
    IconButton,
    Tooltip,
    Snackbar,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
} from '@mui/material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import {Title} from '../../components/ui/Title';
import '../../styles/Fornecedor.css';
import {fetchInsumos} from '../../services/api';

export default function SolicitacaoServico() {
    // Estados principais
    const [selectedDate, setSelectedDate] = useState('');
    const [insumos, setInsumos] = useState([]);
    const [fileAttached, setFileAttached] = useState(false);
    const fileInputRef = useRef(null);
    const [tipoSelecionado, setTipoSelecionado] = useState('');
    const tiposServico = [
        'Movimentação e Pneus',
        'Reforma de Pneus - RECAPE',
        'Corretivo Eletrico',
        'Sinistro',
        'Corretivo Hidraulico',
        'Corretivo Mecânico',
        'Reboque',
    ];

    // Carrega insumos iniciais
    useEffect(() => {
        fetchInsumos().then((response) => setInsumos(response.data));
    }, []);

    // Data máxima para o datepicker
    const [todayString, setTodayString] = useState('');
    useEffect(() => {
        const hoje = new Date();
        const yyyy = hoje.getFullYear();
        const mm = String(hoje.getMonth() + 1).padStart(2, '0');
        const dd = String(hoje.getDate()).padStart(2, '0');
        setTodayString(`${yyyy}-${mm}-${dd}`);
    }, []);

    // Campos do formulário OS
    const [bemVeiculo, setBemVeiculo] = useState('');
    const [contador, setContador] = useState('');
    const [observacoes, setObservacoes] = useState('');
    const [errors, setErrors] = useState({
        bemVeiculo: false,
        tipoServico: false,
        km: false,
        dataOcorrencia: false,
    });

    // Snackbar e contador de OS
    const [numeroOS, setNumeroOS] = useState(1);
    const [mensagemSucesso, setMensagemSucesso] = useState('');
    const [snackbarOpen, setSnackbarOpen] = useState(false);

    // Popup de Incluir Insumo
    const [openInsumoDialog, setOpenInsumoDialog] = useState(false);
    const [newInsumo, setNewInsumo] = useState({
        tipo: '',
        codigo: '',
        nome: '',
        destino: '',
        quantidade: '',
        valorUnitario: '',
        justificativa: '',
    });

    // Handlers
    const handleAttachClick = () => fileInputRef.current?.click();
    const handleFileChange = (e) => setFileAttached(!!e.target.files[0]);

    const handleEnviar = () => {
        const newErrors = {
            bemVeiculo: !bemVeiculo.trim(),
            tipoServico: !tipoSelecionado,
            km: !contador.trim(),
            dataOcorrencia: !selectedDate,
        };
        setErrors(newErrors);
        if (Object.values(newErrors).some(Boolean)) return;

        setMensagemSucesso(`Solicitação enviada como OS${String(numeroOS).padStart(3, '0')}`);
        setNumeroOS((prev) => prev + 1);
        setSnackbarOpen(true);

        // Reset campos
        setBemVeiculo('');
        setTipoSelecionado('');
        setContador('');
        setSelectedDate('');
        setObservacoes('');
        setFileAttached(false);
        setErrors({
            bemVeiculo: false,
            tipoServico: false,
            km: false,
            dataOcorrencia: false,
        });
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handleSnackbarClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setSnackbarOpen(false);
    };

    const parseValor = (valor) => {
        if (!valor) return 0;
        const cleaned = valor
            .toString()
            .replace('R$', '')
            .replace(/\./g, '')
            .replace(',', '.')
            .trim();
        return parseFloat(cleaned) || 0;
    };

    // Insumo dialog
    const handleOpenInsumo = () => setOpenInsumoDialog(true);
    const handleCloseInsumo = () => setOpenInsumoDialog(false);
    const handleChangeNewInsumo = (field) => (e) =>
        setNewInsumo({...newInsumo, [field]: e.target.value});
    const handleAddInsumo = () => {
        setInsumos((prev) => [...prev, {...newInsumo}]);
        setNewInsumo({
            tipo: '',
            codigo: '',
            nome: '',
            destino: '',
            quantidade: '',
            valorUnitario: '',
            justificativa: '',
        });
        setOpenInsumoDialog(false);
    };

    return (
        <>
            {/* Formulário OS */}
            <Paper elevation={1} sx={{p: 3, mb: 3, borderRadius: 3}}>
                <Title>Ordem de Serviço</Title>
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(4, 1fr)',
                        gap: 2,
                        mb: 3,
                    }}
                >
                    {/* Bem/Veículo */}
                    <Box>
                        <Typography variant="subtitle2">Bem/Veículo</Typography>
                        <TextField
                            placeholder="Informe a placa"
                            variant="outlined"
                            fullWidth
                            value={bemVeiculo}
                            onChange={(e) => setBemVeiculo(e.target.value)}
                            error={errors.bemVeiculo}
                        />
                    </Box>

                    {/* Tipo de Serviço */}
                    <Box>
                        <Typography variant="subtitle2">Tipo serviço</Typography>
                        <TextField
                            select
                            value={tipoSelecionado}
                            onChange={(e) => setTipoSelecionado(e.target.value)}
                            fullWidth
                            error={errors.tipoServico}
                            sx={{
                                'opacity': 0.6,
                                'width': '300px',
                                '& .MuiSelect-select span::before': {
                                    content: "'Escolha uma opção'",
                                },
                            }}
                        >
                            {tiposServico.map((tipo) => (
                                <MenuItem key={tipo} value={tipo}>
                                    {tipo}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Box>

                    {/* KM */}
                    <Box>
                        <Typography variant="subtitle2">Contador Km/Hr</Typography>
                        <TextField
                            placeholder="Contador Km/Hr"
                            variant="outlined"
                            fullWidth
                            value={contador}
                            onChange={(e) => setContador(e.target.value)}
                            error={errors.km}
                        />
                    </Box>

                    {/* Data ocorrência */}
                    <Box>
                        <Typography variant="subtitle2">Data ocorrência</Typography>
                        <TextField
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            inputProps={{max: todayString}}
                            helperText="Escolha uma data"
                            fullWidth
                            error={errors.dataOcorrencia}
                        />
                    </Box>

                    {/* Observações, Anexo e Enviar */}
                    <Box
                        sx={{
                            gridColumn: 'span 4',
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: 2,
                        }}
                    >
                        <Box sx={{flex: 1, position: 'relative'}}>
                            <Typography variant="subtitle2" sx={{mb: 0.5}}>
                                Observações
                            </Typography>
                            <TextField
                                multiline
                                rows={2}
                                placeholder="Observações"
                                variant="outlined"
                                fullWidth
                                sx={{pr: '40px'}}
                                value={observacoes}
                                onChange={(e) => setObservacoes(e.target.value)}
                            />
                            <input
                                type="file"
                                style={{display: 'none'}}
                                ref={fileInputRef}
                                onChange={handleFileChange}
                            />
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: '34px',
                                    right: '8px',
                                    textAlign: 'center',
                                }}
                            >
                                <Tooltip
                                    title={fileAttached ? 'Arquivo anexado' : 'Anexar arquivo'}
                                >
                                    <IconButton
                                        onClick={handleAttachClick}
                                        color={fileAttached ? 'success' : 'primary'}
                                        size="medium"
                                        sx={{
                                            border: '1px solid',
                                            borderColor: fileAttached
                                                ? 'success.main'
                                                : 'primary.main',
                                            bgcolor: fileAttached ? 'success.light' : 'transparent',
                                            transition: 'background-color 0.3s, border-color 0.3s',
                                            padding: '6px',
                                        }}
                                    >
                                        {fileAttached ? (
                                            <CheckCircleIcon fontSize="small" />
                                        ) : (
                                            <AttachFileIcon fontSize="small" />
                                        )}
                                    </IconButton>
                                </Tooltip>
                                <Typography
                                    variant="caption"
                                    sx={{
                                        mt: 0.3,
                                        userSelect: 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                    }}
                                >
                                    Anexar
                                </Typography>
                            </Box>
                        </Box>
                        <Box sx={{mt: '38px'}}>
                            <Button
                                variant="contained"
                                className="button-primary"
                                onClick={handleEnviar}
                            >
                                Enviar Solicitação
                            </Button>
                        </Box>
                    </Box>
                </Box>
            </Paper>

            {/* Snackbar de sucesso */}
            <Snackbar
                open={snackbarOpen}
                autoHideDuration={2000}
                onClose={handleSnackbarClose}
                anchorOrigin={{vertical: 'top', horizontal: 'center'}}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    sx={{width: '100%'}}
                    variant="filled"
                >
                    {mensagemSucesso}
                </Alert>
            </Snackbar>

            {/* Seção de Insumos */}
            <Paper sx={{p: 2, mb: 3}}>
                <Typography
                    variant="subtitle1"
                    sx={{
                        fontWeight: 'bold',
                        fontSize: 26,
                        color: '#0d457c',
                        textShadow: '1px 1px 3px rgba(0,0,0,0.2)',
                    }}
                >
                    Insumos
                </Typography>
                <Box sx={{textAlign: 'right', m: 1}}>
                    <Button
                        variant="contained"
                        className="button-primary"
                        onClick={handleOpenInsumo}
                    >
                        Adicionar Insumo
                    </Button>
                </Box>

                {/* Cabeçalho da tabela alinhado */}
                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(8, 1fr)',
                        gap: 2,
                        px: 1,
                        py: 1,
                        fontWeight: 'bold',
                        borderBottom: '2px solid #e5e7eb',
                    }}
                >
                    {[
                        'Tipo',
                        'Codigo',
                        'Nome',
                        'Destino',
                        'Qtd',
                        'Vlr. Unit',
                        'Vlr. Total',
                        'Justificativa',
                    ].map((h) => (
                        <Typography key={h}>{h}</Typography>
                    ))}
                </Box>

                {/* Linhas de dados */}
                {insumos.length > 0 ? (
                    insumos.map((insumo, i) => {
                        const qt = parseFloat(insumo.quantidade) || 0;
                        const vu = parseValor(insumo.valorUnitario);
                        const total = (qt * vu).toFixed(2);
                        return (
                            <Box
                                key={i}
                                sx={{
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(8, 1fr)',
                                    gap: 2,
                                    alignItems: 'center',
                                    py: 1,
                                    px: 1,
                                    borderBottom: '1px solid #e5e7eb',
                                }}
                            >
                                <Typography>{insumo.tipo}</Typography>
                                <Typography>{insumo.codigo}</Typography>
                                <Typography>{insumo.nome}</Typography>
                                <Typography>{insumo.destino}</Typography>
                                <Typography>{insumo.quantidade}</Typography>
                                <Typography>{insumo.valorUnitario}</Typography>
                                <Typography>{total}</Typography>
                            </Box>
                        );
                    })
                ) : (
                    <Box sx={{py: 3, textAlign: 'center', color: '#9CA3AF'}}>
                        <Typography variant="body2">Nenhum item adicionado.</Typography>
                    </Box>
                )}
            </Paper>

            {/* Dialog para incluir insumo */}
            <Dialog open={openInsumoDialog} onClose={handleCloseInsumo} maxWidth="sm" fullWidth>
                <DialogTitle sx={{bgcolor: '#0d457c', color: 'white'}}>
                    Adicionar Novo Insumo
                </DialogTitle>

                <DialogContent sx={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, p: 3}}>
                    {/* Dados básicos */}
                    <TextField
                        label="Tipo"
                        variant="outlined"
                        value={newInsumo.tipo}
                        onChange={handleChangeNewInsumo('tipo')}
                        fullWidth
                    />
                    <TextField
                        label="Código"
                        variant="outlined"
                        value={newInsumo.codigo}
                        onChange={handleChangeNewInsumo('codigo')}
                        fullWidth
                    />

                    {/* Dados detalhados */}
                    <TextField
                        label="Nome do Insumo"
                        variant="outlined"
                        value={newInsumo.nome}
                        onChange={handleChangeNewInsumo('nome')}
                        fullWidth
                        sx={{gridColumn: 'span 2'}}
                    />
                    <TextField
                        label="Destino"
                        variant="outlined"
                        value={newInsumo.destino}
                        onChange={handleChangeNewInsumo('destino')}
                        fullWidth
                    />
                    <TextField
                        label="Quantidade"
                        variant="outlined"
                        type="number"
                        value={newInsumo.quantidade}
                        onChange={handleChangeNewInsumo('quantidade')}
                        fullWidth
                    />
                    <TextField
                        label="Valor Unitário (R$)"
                        variant="outlined"
                        type="number"
                        value={newInsumo.valorUnitario}
                        onChange={handleChangeNewInsumo('valorUnitario')}
                        fullWidth
                    />

                    {/* Justificativa */}
                    <TextField
                        label="Justificativa"
                        variant="outlined"
                        multiline
                        rows={3}
                        value={newInsumo.justificativa}
                        onChange={handleChangeNewInsumo('justificativa')}
                        fullWidth
                        sx={{gridColumn: 'span 2'}}
                    />
                </DialogContent>

                <DialogActions sx={{px: 3, pb: 2}}>
                    <Button onClick={handleCloseInsumo} color="inherit">
                        Cancelar
                    </Button>
                    <Button
                        onClick={() =>
                            setNewInsumo({
                                tipo: '',
                                codigo: '',
                                nome: '',
                                destino: '',
                                quantidade: '',
                                valorUnitario: '',
                                justificativa: '',
                            })
                        }
                    >
                        Limpar Campos
                    </Button>
                    <Button onClick={handleAddInsumo} variant="contained">
                        Incluir
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
}
